import React, { useState, useEffect } from 'react';
import { Select, Typography } from 'antd';
import { Grid, Segment, Header, Button, Icon, Modal, Image } from 'semantic-ui-react';
import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import _ from 'lodash';
import PaymentEnum from '../../constants/PaymentEnum';
import BackButton from '../../components/BackButton';
import Loading from '../../components/Loading';
import GeneralHelper from '../../helpers/general';

const { Text } = Typography;

const TicketPage = (props) => {
	const {
		getTickets,
		getLaundryRooms,
		getPaymentMethods,
		purchaseTicket,
		resetPage,
		resetMeta,
		ticketPageProps,
		getLaundryRoomMachines
	} = props;

	const { loadingTickets, loadingPaymentMethods, payment_methods, tickets, ticket, laundry_rooms ,loadingSubmit, laundry_room_machines } = ticketPageProps;

	// Modal States
	const [openModal, setOpenModal] = useState(false);
	const [topupStatusModal, setTopupStatusModal] = useState(false);

	// Component States
	const [disabled, setDisabled] = useState(true);
	const [paymentMethod, setPaymentMethod] = useState('');
	const [serviceTypes, setServiceTypes] = useState([]);

	const [selectedLaundryRoom, setSelectedLaundryRoom] = useState({});
	const [selectedServiceType, setSelectedServiceType] = useState({});
	const [selectedMinutes, setSelectedMinutes] = useState(0);
	const [selectedQuantity, setSelectedQuantity] = useState(1); // Default quantity is 1
	const [price, setPrice] = useState(0);

	const usedTickets = tickets.filter(ticket => ticket.usage_status === 'used');
	const lastTenUsedTickets = usedTickets.slice(-10)
		.sort((a, b) => new Date(b.used_at) - new Date(a.used_at));

	const expiredTicket = tickets.filter(ticket => moment(ticket?.expire_at)
		.isBefore(moment()) && ticket.usage_status === 'available')
		.sort((a, b) => new Date(b.expire_at) - new Date(a.expire_at));

	useEffect(() => {
		getTickets();
		getLaundryRooms();
		getPaymentMethods();
		getLaundryRoomMachines();

		// Clean-up function
		return () => resetPage();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		checkFields();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedLaundryRoom, selectedServiceType, selectedMinutes, paymentMethod]);

	useEffect(() => {
		if (ticket) {
			setTopupStatusModal(true);
		}
	}, [ticket]);

	const getSelectedLaundryRoom = (val) => {
		// reset fields if laundry room changed
		setSelectedServiceType({});
		setSelectedMinutes(0);
		setSelectedQuantity(1);
		setPrice(0);

		const selectedLaundryRoom = laundry_rooms.filter(laundry_room => laundry_room._id === val)[0];
		setSelectedLaundryRoom(selectedLaundryRoom);

		const service_types = [];
		if (selectedLaundryRoom) {
			const { machines } = selectedLaundryRoom || [];

			for (let i = 0; i < machines.length; i++) {
				const machine = machines[i];
				if(service_types.filter(serviceType => serviceType._id === machine.service_type_id).length > 0) {
					continue;
				}
				service_types.push(machine.service_type);
			}
		}

		setServiceTypes(service_types);
	};

	const getSelectedServiceType = (val) => {
		const selectedServiceType = serviceTypes.filter(serviceType => serviceType._id === val)[0];
		setSelectedServiceType(selectedServiceType);
		setSelectedMinutes(0);
		setPrice(selectedServiceType.price?.price_per_service);
	};

	const checkFields = () => {
		if (
			(_.isEmpty(selectedLaundryRoom) || _.isEmpty(selectedServiceType) || paymentMethod === '') ||
			(selectedServiceType.machine_type === 'dryer' && selectedMinutes === 0)
		) {
			setDisabled(true);
		} else {
			setDisabled(false);
		}
	};

	const proceedToPayment = () => {
		// handle proceed to payment
		const ticketDetails = {
			laundry_room_id: selectedLaundryRoom._id,
			machine_type: selectedServiceType?.machine_type,
			service_type_id: selectedServiceType._id,
			payment_method: paymentMethod,
			bulk_purchase_quantity: selectedQuantity,
			...selectedServiceType?.machine_type === 'dryer' && {number_of_minutes: selectedMinutes}
		};

		purchaseTicket(ticketDetails);
	};

	const modalBuyTicket = () => {
		return (
			<Modal
				closeIcon
				style={{maxWidth: 440}}
				open={openModal}
				onClose={() =>{
					setOpenModal(false);
					setPaymentMethod('');
					setServiceTypes([]);
					setSelectedMinutes(0);
					setSelectedServiceType({});
					setSelectedLaundryRoom({});
					setPrice(0);
					setSelectedQuantity(1);
				} }
			>
				<Header content="Buy Ticket" />
				<Grid style={{ padding: 20 }}>
					<Grid.Row style={{ paddingTop: 0 }}>
						<Grid.Column>
							<div style={{marginTop: 20}}>
								<div style={{marginTop: 20, marginBottom: 20}}>
									<Text> <span style={{ color: 'red' }}>* </span>Laundry Room</Text>
									<Select
										placeholder="Select Room"
										style={{ width: '100%', marginTop: 10 }}
										options={laundry_rooms?.map(laundry_room => {
											return {
												value: laundry_room?._id,
												label: laundry_room?.name
											};
										})}
										onChange={val => getSelectedLaundryRoom(val)}
										required
									/>
								</div>
								<div style={{marginTop: 20, marginBottom: 20}}>
									<Text><span style={{ color: 'red' }}>* </span>Service Type</Text>
									<Select
										disabled={_.isEmpty(selectedLaundryRoom)}
										placeholder="Select Service"
										style={{ width: '100%', marginTop: 10 }}
										options={serviceTypes?.map(serviceType => {
											return {
												value: serviceType?._id,
												label: serviceType?.label
											};
										})}
										onChange={val => getSelectedServiceType(val)}
										value={selectedServiceType._id}
									/>
								</div>
								{
									selectedServiceType.machine_type === 'dryer' &&
									<div style={{marginTop: 20, marginBottom: 20}}>
										<Text><span style={{ color: 'red' }}>* </span>Number of Minutes</Text>
										<Select
											placeholder="Select Minutes"
											style={{ width: '100%', marginTop: 10 }}
											options={(() => {
												const fixedValues = [];
												for(let i = 0; i <= 60; i++) {
													if(i % 5 === 0 && i !== 0) {
														fixedValues.push(i);
													}
												}
												return fixedValues;
											})().map(val => {
												return {
													value: val,
													label: val
												};
											})}
											disabled={(_.isEmpty(selectedServiceType)) || (selectedServiceType.machine_type === 'washer')}
											onChange={(val) => {
												setSelectedMinutes(val);
												setPrice(((val / 5) * selectedServiceType.price?.price_per_service) * selectedQuantity);
											}}
											{...selectedMinutes > 0 && {value: selectedMinutes}}
										/>
									</div>
								}
								<div style={{marginTop: 20, marginBottom: 10}}>
									<Text><span style={{ color: 'red' }}>* </span>Quantity</Text>
									<Select
										disabled={_.isEmpty(selectedLaundryRoom) || _.isEmpty(selectedServiceType) || (selectedServiceType.machine_type === 'dryer' && selectedMinutes === 0)}
										placeholder="Select Quantity"
										style={{ width: '100%', marginTop: 10 }}
										defaultValue={1}
										options={[1,2,3,4,5,6,7,8,9,10].map(quantity => {
											return {
												value: quantity,
												label: quantity
											};
										})}
										onChange={val => {
											setSelectedQuantity(val);
											if (selectedServiceType.machine_type === 'dryer') {
												setPrice(((selectedMinutes / 5) * selectedServiceType.price?.price_per_service) * val);
											} else {
												setPrice(selectedServiceType.price?.price_per_service * val);
											}
										}}
										value={selectedQuantity}
									/>
								</div>
								<p style={{ fontSize: 12, color: '#075185' }}>Ticket validity period: <b>30 days</b></p>
								<div style={{ border: '1px solid #f2f2f2', marginTop: 20, paddingLeft: 10, paddingBottom: 10, paddingRight: 10, borderRadius: 5 }}>
									<div style={{ width: '100%', color: '#ffffff' }}>
										<div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', justifyContent: 'center' }}>
											<div style={{ width: '100%', marginTop: 10, marginBottom: 10 }}>
												<p style={{ fontSize: 12, color: '#075185', width: '100%', textAlign: 'center', fontWeight: 'bold' }}>Select Payment Method</p>
											</div>
											{ loadingPaymentMethods ?
												<Loading/> :
												payment_methods && payment_methods.length && payment_methods.map((pm, index) =>  {
													return (
														<div
															style={{ marginRight: 8 }}
															key={index}
														>
															<Image
																onClick={() => setPaymentMethod(pm.id)}
																src={`${GeneralHelper.getImageBasePath()}/images/${pm.id}.png`}
																className="cust-cursor-pointer"
																style={{
																	height: 40,
																	width: 'auto',
																	border: paymentMethod === pm.id ? '2px solid #1D4FB2' : '1px solid #f2f2f2',
																	boxShadow: '0 0 0 1px rgb(34 36 38 / 15%) inset',
																	borderRadius: '.28571429rem'
																}}
															/>
														</div>
													);
												})
											}
										</div>
									</div>
								</div>
							</div>
						</Grid.Column>
					</Grid.Row>
				</Grid>
				<div style={{ width: '100%', display: 'flex', flexDirection: 'row', border: '1px solid #f2f2f2', padding: 17 }}>
					<div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 5, paddingBottom: 10 }}>
						<div>
							<h4 style={{ fontSize: 11, color: '#999' }}>Total Amount</h4>
						</div>
						<div>
							<h4 style={{ fontSize: 20, color: '#075185', fontWeight: 'bold' }}>
								${parseFloat(price || 0).toFixed(2)}
							</h4>
						</div>
					</div>
					<div>
						<Button
							style={{
								width: '210px',
								border: '2px solid',
								borderColor: '#003366',
								borderRadius: '50px',
								background: '#fff'
							}}
							onClick={() => {
								// setTopup('paid');
								// setTopupStatusModal(true);
								proceedToPayment();
								setDisabled(true);
							}}
							loading={loadingSubmit}
							disabled={disabled}
						>
							<p style={{ display: 'inline-block', color: '#003366' }}>
								Proceed to Payment
								<Icon
									name="arrow right"
									style={{ marginLeft: 10 }}
								/>
							</p>
						</Button>
					</div>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						flexWrap: 'nowrap',
						justifyContent: 'center',
						width: '100%',
						margin: '20px 0px',
						verticalAlign: 'center'
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<p style={{ fontSize: 15, color: '#075185', fontStyle: 'italic' }}>
							Purchased tickets are non-refundable and non-transferrable
						</p>
					</div>
				</div>
			</Modal>
		);
	};

	const modalTopupStatus = () => {
		const { bulk_purchase_quantity, payment_status, _id } = ticket || {};
		return (
			<Modal
				open={topupStatusModal}
				style={{ padding: '20px 20px', textAlign: 'center', width: 200 }}
			>
				{ payment_status === 'paid'
					? <CheckOutlined style={{ backgroundColor: '#008000a1', color: '#fff', fontSize: 35, borderRadius: 40, padding: 15 }} />
					: <CloseOutlined style={{ backgroundColor: 'red', color: '#fff', fontSize: 35, borderRadius: 40, padding: 15 }} /> }
				<h4>{payment_status === 'paid' ? 'Payment Successful' : 'Payment Failed'}</h4>
				<div
					style={{ marginTop: 30, alignSelf: 'center', cursor: 'pointer' }}
					onClick={() => payment_status !== 'paid' || (payment_status === 'paid' && bulk_purchase_quantity > 1) ? setTopupStatusModal(false) : props.history.push(`/tickets/id?id=${_id}`)}
				>
					<p style={{ color: '#7d9bdb', textAlign: 'center' }}>{payment_status === 'paid' ? bulk_purchase_quantity > 1 ? 'OK' : 'Proceed to QR Code' : 'Try Again'}</p>
				</div>
			</Modal>
		);
	};

	return (
		<div
			className="page-wrapper"
			style={{ marginTop: 48 }}
		>
			<Grid container>
				{/* <BackButton
					returnLocation="home"
					returnName="Home"
					{...props}
				/> */}
				<Grid.Row style={{ background: '#fff', borderRadius: 5, boxShadow: '0 2px 5px 0 rgba(0,0,0,0.2), 0 1px 2px 0 rgba(0,0,0,0.19)' }}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							flexWrap: 'nowrap',
							justifyContent: 'space-between',
							width: '100%',
							margin: '0px 15px 20px 15px',
							verticalAlign: 'center'
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<p style={{ fontSize: 15, color: '#075185', fontWeight: 'bold' }}>
								My Tickets
							</p>
						</div>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<p
								onClick={() => props.history.push('/purchase-history')}
								style={{ fontSize: 14, marginRight: '20px', marginTop: '10px', color: '#0088FF', cursor: 'pointer' }}
							>
								Purchase History
							</p>
							<Button
								style={{ width: '180px', border: '1px solid', borderColor: '#003366', borderRadius: '50px', background: '#fff' }}
								onClick={() => setOpenModal(true)}
							>
								<PlusOutlined style={{ fontSize: 16, fontWeight: 'bold', color: '#003366' }} />
								<p style={{ display: 'inline-block', marginLeft: '5px', color: '#003366', fontWeight: 'normal' }}>Buy Ticket</p>
							</Button>
						</div>
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							flexWrap: 'nowrap',
							justifyContent: 'center',
							width: '100%',
							margin: '0px 15px 20px 15px',
							verticalAlign: 'center'
						}}
					>
						<p style={{ fontSize: 15, color: '#075185', fontStyle: 'italic' }}>
							Purchased tickets are non-refundable and non-transferrable
						</p>
					</div>
					<Grid.Column
						tablet={16}
						computer={8}
					>
						<Grid.Row style={{ marginBottom: 10 }}>
							<Grid.Column>
								<Segment>
									<div style={{ margin: '10px' }}>
										<p style={{ fontSize: '14px', color: '#205181', fontWeight: '510' }}>
											Available Tickets
										</p>
									</div>
									<div style={{ minHeight: 500, overflow: 'auto', scrollbarWidth: 'thin' }}>
										{
											tickets.filter(ticket => ticket.usage_status === 'available' && moment(ticket?.expire_at).isAfter(moment())).length > 0 ? tickets.filter(ticket => ticket.usage_status === 'available' && moment(ticket?.expire_at).isAfter(moment())).map(ticket => {
												return (
													<div
														key={ticket._id}
														style={{
															border: '1px solid #D5D5D5',
															borderRight: `5px solid ${ticket.machine_type === 'washer' ? '#63CAE1' : '#A1CD6E'}`,
															display: 'flex',
															flexDirection: 'row',
															padding: '5px 15px',
															alignContent: 'center',
															alignItems: 'center',
															margin: '0px 10px 10px 10px',
															borderRadius: '7px',
															cursor: 'pointer'
														}}
														onClick={() => props.history.push(`/tickets/id?id=${ticket._id}`)}
													>
														<div style={{ flex: 1 }}>
															<div style={{display: 'flex', flexDirection: 'row'}}>
																<small style={{ fontSize: 12, ...ticket.machine_type === 'washer' ? { color: '#63CAE1' } : { color: '#A1CD6E' }, fontWeight: 'bold' }}>{ ticket?.service_type?.label }</small>
															</div>
															<div style={{display: 'flex', flexDirection: 'row'}}>
																<small style={{ fontSize: 11, color: '#8B8B8B', width: 100, fontWeight: 'bold' }}>Activated Machine:</small>
																<small style={{ fontSize: 11, color: '#8B8B8B' }}>{ticket?.selected_machine?.name}</small>
															</div>
															<div style={{display: 'flex', flexDirection: 'row'}}>
																<small style={{ fontSize: 11, color: '#8B8B8B', width: 100, fontWeight: 'bold' }}>Laundry Room:</small>
																<small style={{ fontSize: 11, color: '#8B8B8B' }}>{ticket?.laundry_room?.name}</small>
															</div>
															<div style={{display: 'flex', flexDirection: 'row'}}>
																<small style={{ fontSize: 11, color: '#8B8B8B', width: 100, fontWeight: 'bold' }}>Expiry Date:</small>
																<small style={{ fontSize: 11, color: '#8B8B8B' }}>{moment(ticket?.expire_at).format('MMMM DD, YYYY')}</small>
															</div>
														</div>
														<div>
															<div>
																<Icon
																	name="arrow right"
																	style={{...ticket.machine_type === 'washer' ? { color: '#63CAE1' } : { color: '#A1CD6E' }}}
																// onClick={() => props.history.push('/home')}
																/>
															</div>
														</div>
													</div>
												);
											}) : <div style={{ marginLeft: 10 }}>No available ticket(s)</div>
										}
									</div>
								</Segment>
							</Grid.Column>
							<Grid.Column style={{ marginTop: 15 }}>
								<Segment>
									<div style={{ margin: '10px' }}>
										<p style={{ fontSize: '14px', color: '#205181', fontWeight: '510' }}>
											Machine Status
										</p>
									</div>
									<div style={{ minHeight: 500, padding: 15, overflow: 'auto', scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
										<div>
											{
												laundry_room_machines.length > 0 ? laundry_room_machines.map((laundry_room_machine, index) => {
													return (
														<Segment>
															<div style={{ margin: '10px' }}>
																<p style={{ fontSize: '14px', color: '#205181', fontWeight: '510', textAlign: 'left' }}>
																	{ laundry_room_machine.laundry_room_name }
																</p>
															</div>
															{
																laundry_room_machine.machines && laundry_room_machine.machines.length > 0 && laundry_room_machine.machines.map(machine => {
																	let status = '';

																	if (machine.status === 'enabled' && machine.state === 'available'){
																		status = "Available";
																	}

																	if (machine.status === 'disabled') {
																		status = "Disabled";
																	}

																	if (machine.status === 'on_hold') {
																		status = "On Hold";
																	}

																	if (machine.status === 'maintenance') {
																		status = "Maintenance";
																	}

																	if(machine.state === 'offline') {
																		status = "Offline";
																	}

																	if(machine.state === 'in_use') {
																		status = "In-Use";
																	}
																	return (
																		<div
																			key={index}
																			style={{
																				borderTop: '1px solid #D5D5D5',
																				borderBottom: '1px solid #D5D5D5',
																				borderLeft: '1px solid #D5D5D5',
																				borderRight: `5px solid ${status === 'Available' ? '#00B08D' : (status === 'Disabled'  ||  status === 'Maintenance' || status === 'Offline' || status === 'On Hold') ? '#CCCCCC' : '#FAA61A' }`,
																				display: 'flex',
																				flexDirection: 'row',
																				padding: '5px 15px',
																				alignContent: 'center',
																				alignItems: 'center',
																				margin: '0px 10px 10px 10px',
																				borderRadius: '7px'
																			}}
																		>
																			<div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
																				<div style={{ marginRight: 15 }}>
																					<Image
																						src={`${
																							GeneralHelper.getImageBasePath()}/images/${machine.status === 'enabled' ?
																			ticket?.machine_type === 'washer' ? 'washing-machine-available' : 'dryer-available' :
																							machine.state === 'in_use' ?
																			ticket?.machine_type === 'washer' ? 'washing-machine-in-use' : 'dryer-in-use' :
																		ticket?.machine_type === 'washer' ? 'washing-machine-maintenance' : 'dryer-maintenance'}.svg`}
																						style={{ width: 50, height: 50 }}
																					/>
																				</div>
																				<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
																					<small style={{ fontSize: 12, fontWeight: 'bold', color: '#2B6395' }}>{ machine?.service_type?.label }</small>
																					<small style={{ fontSize: 12, fontWeight: 'bold', color: '#2B6395' }}>{ machine.name }</small>
																					<small
																						style={{
																							fontSize: 11,
																							color: `${status === 'Available' ? '#00B08D' : (status === 'Disabled'  ||  status === 'Maintenance' || status === 'Offline' || status === 'On Hold') ? '#CCCCCC' : '#FAA61A' }`
																						}}
																					>
																						{ `${
																							status === 'In-Use'
																								? (machine.job
																									? status + ` (Last activated ${
																						ticket?.machine_type === 'washer'
																							? `at ${moment(machine?.job?.created_at).format('LLL')})`
																							: `for ${machine?.job?.ticket?.number_of_minutes} min(s) at ${moment(machine?.job?.created_at).format('LLL')})`
																									}`
																									: status
																								)
																								: status
																						}` }
																					</small>
																				</div>
																				<div style={{ width: '100%', display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center'}}>
																					{
																						(machine.status === 'enabled' && machine.state === 'available') && (
																							<Icon
																								name="arrow right"
																								style={{ marginBottom: 5, color: `${status === 'Available' ? '#00B08D' : (status === 'Disabled'  ||  status === 'Maintenance' || status === 'Offline' || status === 'On Hold') ? '#CCCCCC' : '#FAA61A' }` }}
																							/>
																						)
																					}
																				</div>
																			</div>
																		</div>
																	);
																})
															}
														</Segment>
													);
												}) : <div style={{ marginLeft: 10 }}>No available machine(s)</div>
											}
										</div>
									</div>
								</Segment>
							</Grid.Column>
						</Grid.Row>
					</Grid.Column>
					<Grid.Column
						tablet={16}
						computer={8}
					>
						<Grid.Row style={{ marginBottom: 10 }}>
							<Grid.Column>
								<Grid.Column>
									<Segment>
										<div style={{ margin: '10px' }}>
											<p style={{ fontSize: '14px', color: '#205181', fontWeight: '510' }}>
											Last 10 Used Tickets
											</p>
										</div>
										<div style={{ overflow: 'auto', scrollbarWidth: 'thin' }}>
											{
												lastTenUsedTickets.length > 0 ?
													lastTenUsedTickets.map(ticket => {
														return (
															<div
																key={ticket._id}
																style={{
																	border: '1px solid #D5D5D5',
																	display: 'flex',
																	flexDirection: 'row',
																	padding: '5px 15px',
																	alignContent: 'center',
																	alignItems: 'center',
																	margin: '0px 10px 10px 10px',
																	borderRadius: '7px',
																	cursor: 'pointer'
																}}
																onClick={() => props.history.push(`/tickets/id?id=${ticket._id}`)}
															>
																<div style={{ flex: 1 }}>
																	<div style={{display: 'flex', flexDirection: 'row'}}>
																		<small style={{ fontSize: 12, color: '#8B8B8B', fontWeight: 'bold' }}>{ ticket?.service_type?.label }</small>
																	</div>
																	<div style={{display: 'flex', flexDirection: 'row'}}>
																		<small style={{ fontSize: 11, color: '#8B8B8B', width: 100, fontWeight: 'bold' }}>Used Date:</small>
																		<small style={{ fontSize: 11, color: '#8B8B8B' }}>{moment(ticket?.used_at).format('MMMM DD, YYYY')}</small>
																	</div>
																	<div style={{display: 'flex', flexDirection: 'row'}}>
																		<small style={{ fontSize: 11, color: '#8B8B8B', width: 100, fontWeight: 'bold' }}>Laundry Room:</small>
																		<small style={{ fontSize: 11, color: '#8B8B8B' }}>{ticket?.laundry_room?.name}</small>
																	</div>
																</div>
															</div>
														);
													}) : <div style={{ marginLeft: 10, marginBottom: 10 }}>No used ticket(s)</div>
											}
										</div>
									</Segment>
								</Grid.Column>
							</Grid.Column>
						</Grid.Row>
					</Grid.Column>
				</Grid.Row>
			</Grid>
			{modalBuyTicket()}
			{modalTopupStatus()}
		</div>
	);
};

export default TicketPage;
